import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  Button,
  Tooltip,
  Image,
  Space,
  Modal,
  List,
  Typography,
  Avatar
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  PictureOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  KeyOutlined,
  UserOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { getUserVentes } from "../../../services/dashboardclient/dashboardclient";
import { formateCoins } from "../../../function";

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const Ventes = () => {
  const { t } = useTranslation();
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupModalVisible, setBackupModalVisible] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedVente, setSelectedVente] = useState(null);

  // Filtres
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUserVentes();
  }, []);

  const fetchUserVentes = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await getUserVentes({
        per_page: 9999,
        include: ["backups", "venteImages", "utilisateur"],
        ...filters
      });

      if (response.success) {
        setVentes(response.data);
      } else {
        message.error(t("ErrorLoading"));
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error(t("ServerError"));
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const filters = {
      status: statusFilter !== "all" ? statusFilter : undefined,
      start_date: dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
      end_date: dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
      search: searchTerm || undefined,
    };
    
    // Nettoyer les filtres undefined
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== undefined)
    );
    
    fetchUserVentes(cleanedFilters);
  };

  const handleReset = () => {
    setStatusFilter("all");
    setDateRange([]);
    setSearchTerm("");
    fetchUserVentes({});
  };

  const getStatusTag = (status) => {
    switch (status) {
      case "paye":
        return <Tag color="green">{t("Paid")}</Tag>;
      case "enattente":
        return <Tag color="orange">{t("Pending")}</Tag>;
      case "annule":
        return <Tag color="red">{t("Cancelled")}</Tag>;
      default:
        return <Tag color="default">{t("Unknown")}</Tag>;
    }
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  const showBackupCodes = (vente) => {
    setSelectedVente(vente);
    setBackupModalVisible(true);
  };

  const showImages = (vente) => {
    setSelectedVente(vente);
    setImageModalVisible(true);
  };

  // Colonnes du tableau
  const columns = [
    // {
    //   title: t("Client"),
    //   key: "client",
    //   render: (_, record) => {
    //     const user = record.utilisateur;
    //     return (
    //       <Space>
    //         <Avatar
    //           src={user?.avatar}
    //           icon={!user?.avatar && <UserOutlined />}
    //           style={{ backgroundColor: "#87d068" }}
    //         />
    //         <div>
    //           <div style={{ fontWeight: "bold" }}>{user?.username || t("Unknown")}</div>
    //           <Text type="secondary" style={{ fontSize: "12px" }}>
    //             {user?.email || t("No email")}
    //           </Text>
    //         </div>
    //       </Space>
    //     );
    //   },
    // },
    {
      title: t("EA Account/Number"),
      dataIndex: "login",
      key: "login",
      render: (val) => val || "-",
    },
    {
      title: t("EA Password"),
      dataIndex: "password",
      key: "password",
      render: (val) => val ? <Text code>{val}</Text> : "-",
    },
    {
      title: t("Amount"),
      dataIndex: "montant",
      key: "montant",
      render: (val) => {
        if (!val) return t("NA");
        const formatted = new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "USD",
        }).format(val);
        return formatted;
      },
    },
    
    {
      title: t("Coins"),
      dataIndex: "coins",
      key: "coins",
      render: (val) => formateCoins(val) || "-",
    },
    {
      title: t("Account"),
      dataIndex: "compte",
      key: "compte",
      render: (val) => val || "-",
    },
    {
      title: t("Network"),
      dataIndex: "network",
      key: "network",
      render: (val) => val || "-",
    },
    {
      title: t("Date"),
      dataIndex: "date_vente",
      key: "date_vente",
      render: (date) => formatDate(date),
    },
    {
      title: t("Method"),
      dataIndex: ["methode", "nom"],
      key: "methode",
      render: (val) => val || "-",
    },
    {
      title: t("Type"),
      dataIndex: ["type", "nom"],
      key: "type",
      render: (val) => val || "-",
    },
    {
      title: t("Images"),
      dataIndex: "vente_images",
      key: "images",
      render: (vente_images, record) =>
        vente_images && vente_images.length > 0 ? (
          <Button
            type="link"
            icon={<PictureOutlined />}
            onClick={() => showImages(record)}
            size="small"
          >
            {vente_images.length} 
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: t("Backup Codes"),
      dataIndex: "backups",
      key: "backups",
      render: (backups, record) =>
        backups && backups.length > 0 ? (
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => showBackupCodes(record)}
            size="small"
          >
            {backups.length} {t("Codes")}
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: t("Status"),
      dataIndex: "etat",
      key: "etat",
      render: (etat) => getStatusTag(etat),
    },
    {
      title: t("Actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => showImages(record)}
            size="small"
          >
            {t("View")}
          </Button>
          {record.backups && record.backups.length > 0 && (
            <Button
              type="default"
              icon={<KeyOutlined />}
              onClick={() => showBackupCodes(record)}
              size="small"
            >
              {t("Codes")}
            </Button>
          )}
        </Space>
      ),
    }
  ];

  return (
    <Card title={
      <Space>
        <ShoppingCartOutlined style={{color:"var(--primary-color) !important"}}/>
        <span style={{ color: "var(--primary-color)" }}>
          {t("My Sells")}
        </span>
      </Space>
    } style={{ 
      backgroundColor:"var(--background-body)",
      color:"var(--primary-color) !important",
      border: "var(--primary-color6)"
    }}>
      {/* Filtres */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder={t("Status")}
            style={{ width: "100%" }}
            suffixIcon={<FilterOutlined style={{color:"var(--primary-color) !important"}}/>}
          >
            <Option value="all">{t("AllStatuses")}</Option>
            <Option value="paye">{t("Paid")}</Option>
            <Option value="enattente">{t("Pending")}</Option>
            <Option value="annule">{t("Cancelled")}</Option>
          </Select>
        </Col>
        <Col xs={24} sm={8}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Input
            placeholder={t("Search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleFilter}
            suffix={<SearchOutlined />}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleFilter}
          >
            {t("Filter")}
          </Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            {t("Reset")}
          </Button>
        </Col>
      </Row>

      {/* Tableau des ventes */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "50px", marginBottom:"30px"}}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={ventes}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      )}

      {/* Modal pour les Backup Codes */}
      <Modal
        title={
          <Space>
            <KeyOutlined style={{ color: 'var(--primary-color3)' }} />
            <span style={{ color: 'var(--primary-color3)' }}>
              {t("Backup Codes")} - {selectedVente?.type?.nom}
            </span>
          </Space>
        }
        open={backupModalVisible}
        onCancel={() => setBackupModalVisible(false)}
        footer={[
          <Button 
            key="close" 
            onClick={() => setBackupModalVisible(false)}
            style={{ color: 'var(--primary-color3)', borderColor: 'var(--primary-color3)' }}
          >
            {t("Close")}
          </Button>
        ]}
        width={500}
        style={{ color: 'var(--primary-color3)' }}
      >
        {selectedVente?.backups && selectedVente.backups.length > 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--primary-color3)' }}>
            {selectedVente.backups.map((backup) => (
              <div key={backup.id} style={{ margin: '10px 0' }}>
                <Text 
                  copyable 
                  strong 
                  style={{ 
                    fontSize: '16px', 
                    color: 'var(--primary-color3)',
                    fontFamily: 'monospace'
                  }}
                >
                  {backup.code}
                </Text>
              </div>
            ))}
          </div>
        ) : (
          <Text type="secondary" style={{ color: 'var(--primary-color3)' }}>
            {t("NoBackupCodes")}
          </Text>
        )}
      </Modal>

      {/* Modal pour les Images */}
      <Modal
        title={
          <Space>
            <PictureOutlined />
            {t("Sale Images")}
          </Space>
        }
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setImageModalVisible(false)}>
            {t("Close")}
          </Button>
        ]}
        width={800}
      >
        {selectedVente?.vente_images && selectedVente.vente_images.length > 0 ? (
          <Image.PreviewGroup>
            <Row gutter={[16, 16]}>
              {selectedVente.vente_images.map((image) => (
                <Col xs={12} sm={8} key={image.id}>
                  <Image
                    src={`${process.env.REACT_APP_MEDIA_URL}/${image.image}`}
                    
                    alt="Sale proof"
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </Col>
              ))}
            </Row>
          </Image.PreviewGroup>
        ) : (
          <Text type="secondary">{t("NoImages")}</Text>
        )}
      </Modal>
    </Card>
  );
};

export default Ventes;